import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { LiveCityFormDialog } from '@/features/admin/components/LiveCityFormDialog';
import {
  destinationsApi,
  destinationsKeys,
  type AdminDestinationsParams,
} from '@/features/destinations/api/destinations.api';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { Destination } from '@/types';

type LiveFilter = NonNullable<AdminDestinationsParams['isLive']>;

export function AdminLiveCitiesPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [liveFilter, setLiveFilter] = useState<LiveFilter>('true');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Destination | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const params: AdminDestinationsParams = {
    page: page + 1,
    limit: rowsPerPage,
    isLive: liveFilter,
    ...(debouncedSearch && { search: debouncedSearch }),
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: destinationsKeys.admin(params),
    queryFn: () => destinationsApi.adminList(params),
  });

  const liveMutation = useMutation({
    mutationFn: ({ id, isLive }: { id: string; isLive: boolean }) =>
      destinationsApi.update(id, { isLive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: destinationsKeys.all });
    },
  });

  const cities = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  const openDialog = (city: Destination | null) => {
    setEditing(city);
    setDialogOpen(true);
  };

  return (
    <Box>
      <AdminPageHeader
        title="Live Cities"
        subtitle="Cities shown in the home page search, live-city chips and experience cards"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add city
          </Button>
        }
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search city or state"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Show"
          value={liveFilter}
          onChange={(e) => {
            setLiveFilter(e.target.value as LiveFilter);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="true">Live</MenuItem>
          <MenuItem value="false">Not live</MenuItem>
          <MenuItem value="all">All destinations</MenuItem>
        </TextField>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Failed to load cities.
        </Alert>
      )}
      {liveMutation.isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {(liveMutation.error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Could not update the city.'}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>City</TableCell>
                <TableCell>Highlight</TableCell>
                <TableCell align="center">Order</TableCell>
                <TableCell align="center">Live</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && cities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {liveFilter === 'true'
                        ? 'No live cities yet. Add one to show it on the home page.'
                        : 'No cities match.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {cities.map((city) => (
                <TableRow key={city._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        component={city.imageUrl ? 'img' : 'div'}
                        src={city.imageUrl || undefined}
                        alt=""
                        sx={{
                          width: 56,
                          height: 40,
                          borderRadius: 1,
                          flexShrink: 0,
                          bgcolor: 'action.hover',
                          objectFit: 'cover',
                        }}
                      />
                      <Box>
                        <Typography fontWeight={600}>{city.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {city.region || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {city.highlight || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{city.sortOrder ?? 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={city.imageUrl ? '' : 'Add an image before going live'}>
                      <span>
                        <Switch
                          checked={Boolean(city.isLive)}
                          disabled={liveMutation.isPending || (!city.isLive && !city.imageUrl)}
                          onChange={(_, checked) =>
                            liveMutation.mutate({ id: city._id, isLive: checked })
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" aria-label="Edit city" onClick={() => openDialog(city)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>

      <LiveCityFormDialog open={dialogOpen} city={editing} onClose={() => setDialogOpen(false)} />
    </Box>
  );
}
